import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { BookOpenText } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// 竞赛/活动规则 PDF：唯一来源 public/documents/rules.pdf。
// 加载失败则显示可读错误与「重試」按钮（EN-01）。不做自动降级——assets/document 下那份是
// 另一个日期戳的独立文件，无同步机制，静默切过去会让用户读到过期规则。
const publicPdfUrl = `${import.meta.env.BASE_URL ?? '/'}documents/rules.pdf`.replace(/\/{2,}/g, '/');

// 手机横屏(高≤500)判定：与 CSS [@media(max-height:500px)] 同断点，用于横屏时去掉
// ui/dialog 默认注入的空标题(showTitle)。本地实现，避免把 dashboard 外壳耦合进登录包。
function useShortViewport() {
  const [is, setIs] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-height: 500px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-height: 500px)');
    const on = () => setIs(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return is;
}

// 把传入的子元素作为 DialogTrigger，点击后弹出比赛/活动详情 PDF。
// 登录页顶部「活動介紹」胶囊与其他入口共用同一份内容。
export function CompetitionRulesDialog({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const isShort = useShortViewport();
  const [failed, setFailed] = useState(false);
  const retry = () => setFailed(false);
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {/* 手机横屏(高≤500)：镜像 dashboard 顶栏「規則」弹窗，等比缩到 292×374 贴合 390 舞台，
          去掉默认空标题、收紧内边距/间距；仅 max-height:500px 生效，桌面 showTitle 仍为 true，不动电脑/平板。 */}
      <DialogContent
        showTitle={!isShort}
        className="sm:max-w-3xl h-[85vh] flex flex-col [@media(max-height:500px)]:!w-[292px] [@media(max-height:500px)]:!max-w-[292px] [@media(max-height:500px)]:!h-[min(374px,calc(100vh-1rem))] [@media(max-height:500px)]:!px-4 [@media(max-height:500px)]:!pt-4 [@media(max-height:500px)]:!pb-4 [@media(max-height:500px)]:!gap-2.5"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <BookOpenText className="h-5 w-5 text-primary" />
            {t("user.competition-rules-title")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto rounded-lg border">
          {failed ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-muted-foreground">{t('common.error', { defaultValue: '錯誤' })}</p>
              <button
                type="button"
                onClick={retry}
                className="rounded-full px-5 py-1.5 text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: 'var(--sidebar-active)' }}
              >
                {t('common.retry', { defaultValue: '重試' })}
              </button>
            </div>
          ) : (
            <iframe
              key={publicPdfUrl}
              src={publicPdfUrl}
              className="w-full h-full"
              title={t("user.competition-rules-title")}
              onLoad={(e) => {
                // 不能用 onError：rules.pdf 被删/改名时同源 404 命中 SPA 兜底路由，服务器返回
                // 200 + index.html，浏览器判定为一次「成功」加载，只触发 load，从不触发 error。
                // 因此改为检查这次已完成加载的文档类型，非 PDF 即判失败；不发任何额外网络请求。
                try {
                  const doc = e.currentTarget.contentDocument;
                  // 挂载时可能先为初始 about:blank 空文档触发一次 load（contentType 为 text/html），
                  // 必须跳过，否则 PDF 正常时也会误报失败。
                  if (!doc || doc.URL === 'about:blank') return;
                  if (!/pdf/i.test(doc.contentType ?? '')) setFailed(true);
                } catch { /* 取不到文档（跨源/插件视图）视为正常，不误报 */ }
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CompetitionRulesDialog;
